import { lintGutter, linter } from "@codemirror/lint";
import { Compartment, RangeSet, RangeValue, StateEffect, StateField } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { lintSourceConfig } from "components/organisms/editor/codemirror/linting";

const compileLintSourceConf = new Compartment();

class CompileDiagnosticRangeValue extends RangeValue {
    constructor(diagnostic) {
        super();
        this.diagnostic = diagnostic;
    }
}

const setCompileDiagnosticsEffect = StateEffect.define();

export const compileDiagnosticsState = StateField.define({
    create() {
        return RangeSet.empty;
    },
    update(value, transaction) {
        for (const effect of transaction.effects) {
            if (effect.is(setCompileDiagnosticsEffect)) {
                return RangeSet.of(
                    effect.value.map((diagnostic) =>
                        new CompileDiagnosticRangeValue(diagnostic).range(diagnostic.from, diagnostic.to),
                    ),
                    true,
                );
            }
        }

        if (transaction.docChanged) {
            value = value.map(transaction.changes);
        }

        return value;
    },
});

function compileLogLintSource() {
    return linter((view) => {
        const items = [];
        const cursor = view.state.field(compileDiagnosticsState).iter();

        while (cursor.value !== null) {
            const { diagnostic } = cursor.value;
            items.push({
                ...diagnostic,
                from: cursor.from,
                to: cursor.to,
            });
            cursor.next();
        }

        items.sort((left, right) => left.from - right.from || left.entryIndex - right.entryIndex);
        return items;
    }, lintSourceConfig);
}

export function compileAnnotations() {
    return [
        compileDiagnosticsState,
        compileLintSourceConf.of(compileLogLintSource()),
        lintGutter({
            hoverTime: 0,
        }),
        EditorView.baseTheme({
            ".cm-gutter-lint": {
                order: -1,
                width: "10px",
            },
            ".cm-gutter-lint .cm-gutterElement": {
                padding: "0 2px 0 0",
            },
            ".cm-lint-marker-error": {
                width: "6px",
                height: "6px",
                borderRadius: "999px",
                background: "#d94b4b",
                boxShadow: "0 0 0 3px rgba(217, 75, 75, 0.14)",
            },
            ".cm-lint-marker-warning": {
                width: "6px",
                height: "6px",
                borderRadius: "999px",
                background: "#b98517",
                boxShadow: "0 0 0 3px rgba(185, 133, 23, 0.12)",
            },
            ".cm-diagnostic": {
                borderLeft: "2px solid #d94b4b",
            },
            ".cm-diagnostic.cm-diagnostic-warning": {
                borderLeftColor: "#b98517",
            },
        }),
    ];
}

export function setAnnotations(state, annotations) {
    const diagnostics = [];

    (Array.isArray(annotations) ? annotations : []).forEach((annotation) => {
        if (annotation.row == null || annotation.row < 0) return;

        try {
            const line = state.doc.line(annotation.row + 1);
            diagnostics.push({
                from: line.from,
                to: line.to,
                severity: annotation.type === "error" ? "error" : "warning",
                message: annotation.text || "",
                source: annotation.source || "compile",
                ruleId: annotation.ruleId,
                id: annotation.id,
                entryIndex: annotation.entryIndex || 0,
                firstOnLine: annotation.firstOnLine,
            });
        } catch (_error) {
            // ignore invalid annotation positions
        }
    });

    return {
        effects: setCompileDiagnosticsEffect.of(diagnostics),
    };
}

export function showCompileLogDiagnostics(show) {
    return {
        effects: [compileLintSourceConf.reconfigure(show ? compileLogLintSource() : [])],
    };
}
